import React from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { ExclamationCircleIcon, ClockIcon } from '@heroicons/react/24/outline';
import useExportEstimate from '../hooks/useExportEstimate';

/**
 * Pre-flight CSV export confirmation modal (feature 017).
 *
 * Renders 4 states based on `useExportEstimate`:
 *  1. Loading       — skeleton + disabled confirm
 *  2. Under-limit   — `Estimated: ~Xm Ys` + enabled confirm
 *  3. Over-limit    — RED banner (FR-018 verbatim) + DISABLED confirm
 *  4. Probe error   — YELLOW banner (FR-019 verbatim) + ENABLED confirm
 *
 * Tailwind only — no Material-UI. Heroicons for iconography.
 */

export function formatDuration(totalSeconds) {
  const safe = Math.max(0, Math.round(Number(totalSeconds) || 0));
  const m = Math.floor(safe / 60);
  const s = safe % 60;
  return `${m}m ${s}s`;
}

const OVER_LIMIT_COPY = (xmys) => (
  `This export would take ~${xmys}, exceeding the 12-minute limit. `
  + 'Please narrow your date range or split it into smaller exports.'
);
const ESTIMATE_UNAVAILABLE_COPY = 'Could not estimate — proceed at your own risk.';

const ExportConfirmationModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  action,
  projectId,
  dateIni,
  dateEnd,
  filters,
  serialNumber,
  object,
  queryParams,
  totalRows,
  confirmText = 'Export CSV',
  cancelText = 'Cancel',
}) => {
  const estimate = useExportEstimate({
    projectId,
    action,
    dateIni,
    dateEnd,
    filters,
    serialNumber,
    object,
    queryParams,
    totalRows,
    enabled: isOpen,
  });

  if (!isOpen) return null;

  const { isLoading, isError, data } = estimate;
  const willExceedLimit = Boolean(data?.willExceedLimit);
  const estimateLabel = data ? `~${formatDuration(data.estimatedSeconds)}` : null;
  const confirmDisabled = isLoading || willExceedLimit;

  const renderBody = () => {
    if (isLoading) {
      return (
        <div className="space-y-3" data-testid="export-estimate-skeleton">
          <div className="flex items-center space-x-2 text-sm text-gray-700">
            <svg
              className="animate-spin h-4 w-4 text-indigo-600 flex-shrink-0"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
              />
            </svg>
            <span>Calculating estimate…</span>
          </div>
          <div className="h-3 bg-gray-200 rounded animate-pulse w-3/4" />
          <div className="h-3 bg-gray-200 rounded animate-pulse w-1/2" />
        </div>
      );
    }

    if (isError) {
      return (
        <div
          className="flex items-center space-x-2 rounded-md border border-amber-300 bg-amber-50 p-4 text-sm text-amber-800"
          data-testid="export-estimate-warning"
          role="alert"
        >
          <ClockIcon className="h-5 w-5 text-amber-500" aria-hidden="true" />
          <span>{ESTIMATE_UNAVAILABLE_COPY}</span>
        </div>
      );
    }

    if (willExceedLimit) {
      return (
        <div
          className="flex items-start space-x-3 rounded-md border border-red-300 bg-red-50 p-4"
          role="alert"
          data-testid="export-estimate-over-limit"
        >
          <ExclamationCircleIcon className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" aria-hidden="true" />
          <p className="text-sm text-red-800">{OVER_LIMIT_COPY(formatDuration(data.estimatedSeconds))}</p>
        </div>
      );
    }

    return (
      <div className="flex items-center space-x-2 text-sm text-gray-700" data-testid="export-estimate-ok">
        <ClockIcon className="h-5 w-5 text-gray-500" aria-hidden="true" />
        <span>
          Estimated: <span className="font-medium text-gray-900">{estimateLabel}</span>
        </span>
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            className="fixed inset-0 z-50 bg-gray-900 bg-opacity-75 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            className="fixed inset-0 z-[60] flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.95, y: '-20%' }}
            animate={{ opacity: 1, scale: 1, y: '0%', transition: { type: 'spring', damping: 20, stiffness: 300 } }}
            exit={{ opacity: 0, scale: 0.95, y: '-20%', transition: { duration: 0.2 } }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="export-modal-title"
          >
            <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-auto">
              <h3 id="export-modal-title" className="text-lg font-medium leading-6 text-gray-900 mb-4">
                {title}
              </h3>
              <div className="mt-2 mb-6">{renderBody()}</div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  {cancelText}
                </button>
                <button
                  type="button"
                  data-testid="export-confirm-button"
                  disabled={confirmDisabled}
                  onClick={() => {
                    if (!confirmDisabled) {
                      onConfirm();
                      onClose();
                    }
                  }}
                  className={`px-4 py-2 text-sm font-medium text-white border border-transparent rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 ${
                    confirmDisabled
                      ? 'bg-indigo-300 cursor-not-allowed'
                      : 'bg-indigo-600 hover:bg-indigo-700'
                  }`}
                >
                  {confirmText}
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

ExportConfirmationModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  title: PropTypes.string.isRequired,
  action: PropTypes.oneOf([
    'brandConsumptions',
    'machineTelemetryConsumption',
    'telemetryObject',
    'lifeCycleObject',
    'sandboxObject',
  ]).isRequired,
  projectId: PropTypes.number.isRequired,
  dateIni: PropTypes.string.isRequired,
  dateEnd: PropTypes.string.isRequired,
  filters: PropTypes.string,
  serialNumber: PropTypes.string,
  object: PropTypes.string,
  queryParams: PropTypes.string,
  totalRows: PropTypes.number,
  confirmText: PropTypes.string,
  cancelText: PropTypes.string,
};

export default ExportConfirmationModal;
